import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@state/store';
import { setActiveScenario, createScenario } from '@state/slices/studySlice';
import { validateCfdSetup } from '../../validation/cfdValidation';

/**
 * StudyOverviewInspector (Gate 1 Foundation)
 *
 * Exposes study configuration, scenario switching, and high-level CFD setup summary.
 */
export const StudyOverviewInspector: React.FC = () => {
  const dispatch = useAppDispatch();
  const study = useAppSelector((state) => state.study.study);
  const activeScenarioId = study.activeScenarioId;
  const activeScenario = study.scenarios[activeScenarioId];
  const geometryCount = useAppSelector((state) => state.geometry.ids.length);

  const [newScenarioName, setNewScenarioName] = useState('');
  const [showAddScenario, setShowAddScenario] = useState(false);

  const validation = validateCfdSetup(activeScenario?.cfdSetup, geometryCount);

  const handleCreateScenario = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newScenarioName.trim()) return;
    const id = `scenario-${Date.now().toString(36)}`;
    dispatch(createScenario({ id, name: newScenarioName.trim() }));
    setNewScenarioName('');
    setShowAddScenario(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '11.5px' }}>
      {/* Header Info */}
      <div style={{ borderBottom: '1px solid rgba(47, 120, 196, 0.12)', paddingBottom: '8px' }}>
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: '9px',
            fontWeight: 700,
            color: '#2F78C4',
            letterSpacing: '0.12em',
            marginBottom: '2px'
          }}
        >
          STUDY WORKSPACE
        </div>
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
          {study.name}
        </div>
        <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '2px' }}>
          {study.description}
        </div>
      </div>

      {/* Scenario Selector */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ fontWeight: 600, color: '#334155' }}>Active Scenario</label>
          <button
            type="button"
            onClick={() => setShowAddScenario((prev) => !prev)}
            style={{
              fontSize: '10px',
              color: '#2450A6',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              padding: '0 2px'
            }}
          >
            {showAddScenario ? 'Cancel' : '+ New Scenario'}
          </button>
        </div>

        {showAddScenario ? (
          <form onSubmit={handleCreateScenario} style={{ display: 'flex', gap: '4px' }}>
            <input
              type="text"
              placeholder="Scenario name..."
              value={newScenarioName}
              onChange={(e) => setNewScenarioName(e.target.value)}
              style={{
                flex: 1,
                height: '26px',
                padding: '0 6px',
                fontSize: '11px',
                border: '1px solid rgba(47, 120, 196, 0.3)',
                borderRadius: '3px'
              }}
              autoFocus
            />
            <button
              type="submit"
              style={{
                padding: '0 8px',
                background: '#2450A6',
                color: '#fff',
                border: 'none',
                borderRadius: '3px',
                fontSize: '10px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Add
            </button>
          </form>
        ) : (
          <select
            value={activeScenarioId}
            onChange={(e) => dispatch(setActiveScenario(e.target.value))}
            style={{
              height: '28px',
              padding: '0 8px',
              borderRadius: '3px',
              border: '1px solid rgba(47, 120, 196, 0.25)',
              background: '#ffffff',
              fontSize: '11.5px',
              color: '#0f172a',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            {Object.values(study.scenarios).map((sc) => (
              <option key={sc.id} value={sc.id}>
                {sc.name} {sc.isBaseline ? '(Baseline)' : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Setup Summary Snapshot Card */}
      <div
        style={{
          border: '1px solid rgba(47, 120, 196, 0.18)',
          borderRadius: '4px',
          background: 'rgba(248, 246, 241, 0.85)',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}
      >
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: '#475569'
          }}
        >
          CFD SETUP SPECIFICATION SUMMARY
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '10.5px' }}>
          <div>
            <div style={{ color: '#64748b' }}>Wind</div>
            <strong
              style={{
                fontFamily: 'ui-monospace, monospace',
                color: activeScenario?.cfdSetup.wind.speed !== null ? '#0f172a' : '#b45309'
              }}
            >
              {activeScenario?.cfdSetup.wind.speed !== null && activeScenario?.cfdSetup.wind.direction !== null
                ? `${activeScenario.cfdSetup.wind.speed.toFixed(1)} m/s @ ${activeScenario.cfdSetup.wind.direction.toFixed(0)}°`
                : activeScenario?.cfdSetup.wind.speed !== null
                ? `${activeScenario.cfdSetup.wind.speed.toFixed(1)} m/s (direction unset)`
                : 'Not specified'}
            </strong>
          </div>
          <div>
            <div style={{ color: '#64748b' }}>Domain Extents</div>
            <strong style={{ fontFamily: 'ui-monospace, monospace', color: '#0f172a' }}>
              {activeScenario?.cfdSetup.domain.width} × {activeScenario?.cfdSetup.domain.length} × {activeScenario?.cfdSetup.domain.height} m
            </strong>
          </div>
          <div>
            <div style={{ color: '#64748b' }}>Site Geometry</div>
            <strong style={{ fontFamily: 'ui-monospace, monospace', color: '#0f172a' }}>
              {geometryCount} building masses
            </strong>
          </div>
          <div>
            <div style={{ color: '#64748b' }}>Preflight Status</div>
            <strong
              style={{
                fontFamily: 'ui-monospace, monospace',
                color: validation.isReady ? '#16a34a' : '#d97706'
              }}
            >
              {validation.isReady ? 'READY FOR ENGINE SPECIFICATION' : 'INCOMPLETE'}
            </strong>
          </div>
        </div>
      </div>

      {/* Gate 2 Workflow Sequence Card */}
      <div
        style={{
          borderLeft: '3px solid #2450A6',
          padding: '6px 10px',
          background: 'rgba(36, 80, 166, 0.05)',
          borderRadius: '0 3px 3px 0',
          fontSize: '10px',
          color: '#334155',
          lineHeight: 1.4
        }}
      >
        <div style={{ fontWeight: 600, color: '#1e3a8a', marginBottom: '2px' }}>
          Gate 2 Scientific Architecture Sequence
        </div>
        <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '9px', color: '#475569' }}>
          Study Setup → Validation → Engine Selection → Run Specification Snapshot → Engine Adapter → Normalized CFD Result
        </div>
      </div>
    </div>
  );
};

export default StudyOverviewInspector;
